/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-unused-styles */
import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Image, ScrollView } from 'react-native';
import QText from './QText';
import { useTheme } from 'react-native-paper';
import { useSelector, useDispatch } from 'react-redux';
import { Portal, Modal, Button, Paragraph, Dialog } from 'react-native-paper';
import { useOrganizationTranslation } from '../hooks/useOrganizationTranslation';
import NotificationsIcon from './Icons/NotificationsIcon';
import ReportIcon from './Icons/ReportIcon';
import SettingIcon from './Icons/SettingIcon';
import HelpIcon from './Icons/HelpIcon';
import FeedbackIcon from './Icons/FeedbackIcon';
import TemplateIcon from './Icons/TemplateIcon';
import WhatsAppIcon from './Icons/WhatsAppIcon';
import WhatsAppTemplateIcon from './Icons/WhatsAppTemplateIcon';
import AttachmentIcon from './Icons/AttachmentIcon';
import HistoryIcon from './Icons/HistoryIcon';
import { getAvatarLabel, API_BASE_URL } from '../utils';
import { Avatar } from 'react-native-paper';
import { getBusinessProfileImageUrl } from '../utils/imageUpload';
import LogOutIcon from './Icons/LogOutIcon';
import UserIcon from './Icons/UserIcon';
import { getFcmToken, handleLogout } from '../utils';
import { useLogoutMutation } from '../store/api/UserApi';
import DrawerSection from './DrawerSection';
import DrawerMenuItem from './DrawerMenuItem';
import ModeToggle from './ModeToggle';
import QrscannerIcon from './Icons/QrscannerIcon';
import OrdersIcon from './Icons/OrdersIcon';

const DrawerMenu = ({
  showDrawer,
  onDismiss,
  navigationAction,
  notificationCount,
}) => {
  const { t, i18n } = useOrganizationTranslation();
  const user = useSelector((state) => state.user);
  const role = useSelector((state) => state.role);
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [logout] = useLogoutMutation();

  const theme = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const showDialog = () => setVisible(true);
  const hideDialog = () => setVisible(false);

  // Get avatar image source for business profile
  const getAvatarImageSource = () => {
    if (role === 'business' && user?.business?.profile_image_url) {
      return user.business.profile_image_url;
    }
    return null;
  };

  const getDisplayName = () => {
    if (role === 'business' && user?.business?.name) {
      return user.business.name;
    }
    return user?.name;
  };

  const logoutHandler = async () => {
    hideDialog();
    const fcmToken = await getFcmToken();
    await handleLogout({
      logout,
      i18n,
      dispatch,
      fcmToken,
    });
  };

  const createTwoButtonAlert = () => {
    return (
      <Portal>
        <Dialog
          visible={visible}
          onDismiss={hideDialog}
          style={{
            alignSelf: 'center',
            maxWidth: 350,
            borderRadius: 8,
            backgroundColor: theme.colors.onBackground,
          }}
        >
          <Dialog.Title style={{ color: theme.colors.secondaryText }}>
            {t('alert_title')}
          </Dialog.Title>
          <Dialog.Content>
            <Paragraph style={{ color: theme.colors.secondaryText }}>
              {t('alert_logout_msg')}
            </Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              onPress={hideDialog}
              labelStyle={{ color: theme.colors.placeHolderText }}
            >
              {t('no')}
            </Button>
            <Button onPress={logoutHandler}>{t('yes')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    );
  };

  return (
    <Portal>
      {createTwoButtonAlert()}
      <Modal
        visible={showDrawer}
        onDismiss={onDismiss}
        contentContainerStyle={styles.drawerContainer}
      >
        <View style={styles.bgContainer}>
          {/* Profile Header */}
          <View style={styles.headerContainer}>
            <View style={styles.profileSection}>
              {getAvatarImageSource() && !imageLoadError ? (
                <View style={styles.avatarContainer}>
                  <Image
                    source={{
                      uri: getAvatarImageSource(),
                      cache: 'default',
                    }}
                    style={styles.avatarImage}
                    onError={() => {
                      setImageLoadError(true);
                    }}
                  />
                </View>
              ) : (
                <View style={[styles.avatarContainer, styles.avatarText]}>
                  <QText style={styles.avatarLabel}>
                    {getAvatarLabel(getDisplayName())}
                  </QText>
                </View>
              )}
              <View style={styles.profileInfo}>
                {getDisplayName() ? (
                  <QText style={styles.userName}>{getDisplayName()}</QText>
                ) : (
                  <QText style={styles.userName}>{t('not_available')}</QText>
                )}
                <QText style={styles.roleText}>
                  {role === 'business'
                    ? t('business_account')
                    : t('user_account')}
                </QText>
              </View>
            </View>
          </View>

          {/* Mode Toggle Section */}
          <ModeToggle onModeChange={onDismiss} />

          <ScrollView
            style={styles.menuContainer}
            showsVerticalScrollIndicator={false}
          >
            {/* Core Navigation */}
            <DrawerSection>
              <DrawerMenuItem
                icon={NotificationsIcon}
                title={t('notifications')}
                onPress={() => navigationAction('NotificationScreen')}
                badge={
                  notificationCount > 0 ? notificationCount.toString() : null
                }
              />
              <DrawerMenuItem
                icon={OrdersIcon}
                title={t('orders')}
                onPress={() =>
                  navigationAction(
                    role === 'business' ? 'OrdersScreen' : 'OrdersUserScreen'
                  )
                }
              />
              {role === 'user' && (
                <DrawerMenuItem
                  icon={AttachmentIcon}
                  title={t('attachments')}
                  onPress={() => navigationAction('AttachmentScreen')}
                />
              )}
              {role === 'business' && (
                <DrawerMenuItem
                  icon={ReportIcon}
                  title={t('report')}
                  onPress={() => navigationAction('ReportScreen')}
                />
              )}
            </DrawerSection>

            {/* Communication Section */}
            {role === 'business' && (
              <DrawerSection title={t('communication')}>
                <DrawerMenuItem
                  icon={WhatsAppTemplateIcon}
                  title={t('whatsapp_templates')}
                  onPress={() => navigationAction('TemplateListScreen')}
                />
                <DrawerMenuItem
                  icon={WhatsAppIcon}
                  title={t('campaigns.title')}
                  onPress={() => navigationAction('CampaignsScreen')}
                />
              </DrawerSection>
            )}

            {/* Account & Settings */}
            <DrawerSection>
              <DrawerMenuItem
                icon={UserIcon}
                title={t('profile')}
                onPress={() => navigationAction('ProfileScreen')}
              />
              {role === 'business' && (
                <DrawerMenuItem
                  icon={QrscannerIcon}
                  title={t('business_qr')}
                  onPress={() => navigationAction('ProfileScreen')}
                />
              )}
              <DrawerMenuItem
                icon={SettingIcon}
                title={t('settings')}
                onPress={() => navigationAction('SettingScreen')}
              />
            </DrawerSection>

            {/* Help & Support */}
            <DrawerSection isLast>
              <DrawerMenuItem
                icon={HelpIcon}
                title={t('help_and_support')}
                onPress={() => navigationAction('SupportScreen')}
              />
              <DrawerMenuItem
                icon={FeedbackIcon}
                title={t('feedback')}
                onPress={() => navigationAction('FeedbackScreen')}
              />
            </DrawerSection>
          </ScrollView>

          {/* Logout Section */}
          <View style={styles.logoutContainer}>
            <DrawerMenuItem
              icon={LogOutIcon}
              title={t('logout')}
              onPress={showDialog}
              variant="danger"
            />
          </View>
        </View>
      </Modal>
    </Portal>
  );
};
const createStyles = (theme) =>
  StyleSheet.create({
    drawerContainer: {
      flex: 1,
      width: 280,
      backgroundColor: theme.colors.background,
    },
    bgContainer: {
      flex: 1,
      backgroundColor: theme.colors.onBackground,
      display: 'flex',
      flexDirection: 'column',
    },
    headerContainer: {
      paddingHorizontal: 20,
      paddingVertical: 24,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.outline,
    },
    profileSection: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    avatarContainer: {
      width: 48,
      height: 48,
      borderRadius: 4,
      overflow: 'hidden',
      backgroundColor: 'transparent',
    },
    avatarImage: {
      width: '100%',
      height: '100%',
    },
    avatarText: {
      backgroundColor: theme.colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
    },
    avatarLabel: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.whiteTxt,
    },
    profileInfo: {
      marginLeft: 16,
      flex: 1,
    },
    userName: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.secondaryText,
      marginBottom: 2,
    },
    roleText: {
      fontSize: 13,
      color: theme.colors.placeHolderText,
      textTransform: 'capitalize',
    },
    menuContainer: {
      flex: 1,
      paddingTop: 8,
    },
    logoutContainer: {
      borderTopWidth: 1,
      borderTopColor: theme.colors.outline,
      paddingVertical: 8,
    },
  });
export default DrawerMenu;
